import React, { useState, useEffect, useCallback, useRef } from "react";
import { Typography, Flex, Input, Button, Table, Tag, Tooltip, Empty, message, Spin, DatePicker } from "antd";
import { SearchOutlined, DownloadOutlined, MailOutlined, FormOutlined, EyeOutlined } from "@ant-design/icons";
import { getProposals, getProposalSummary } from "../../../services/proposalService";
import { getClients } from "../../../services/clientService";
import useAuthSession from "../../../hooks/useAuthSession";
import ProposalGeneratorModal from "../proposals/ProposalGeneratorModal";

const { Title, Text } = Typography;

function extractProposalCompanies(proposalsList, clientsList) {
  const compMap = new Map();
  proposalsList.forEach((p) => {
    const rawComp = (p.leadCompanyName || p.companyName || "").trim();
    const rawContact = (p.contactName || "").trim();
    const key = (rawComp || p.clientId || p.leadId || "Client Enterprise").toLowerCase();

    if (!compMap.has(key)) {
      const matchedClient = clientsList.find(
        (c) =>
          (c.companyName && c.companyName.trim().toLowerCase() === rawComp.toLowerCase()) ||
          (c.clientId && p.clientId && c.clientId === p.clientId)
      );

      compMap.set(key, {
        key,
        clientId: p.clientId || matchedClient?.clientId || p.leadId || key,
        leadId: p.leadId || matchedClient?.leadId || "",
        companyName: rawComp || matchedClient?.companyName || "Client Enterprise",
        leadCompanyName: rawComp || matchedClient?.companyName || "Client Enterprise",
        primaryContact: rawContact || matchedClient?.primaryContactName || matchedClient?.contactName || "Contact Person",
        phone: p.contactNumber || matchedClient?.primaryPhone || matchedClient?.phone || "",
        email: p.directorEmailAddress || matchedClient?.primaryEmail || matchedClient?.email || "",
        proposalCount: 1,
      });
    } else {
      compMap.get(key).proposalCount += 1;
    }
  });

  return Array.from(compMap.values()).sort((a, b) => b.proposalCount - a.proposalCount);
}

function buildProposalQueryOptions({ page, pageSize, signal, employeeId, selectedLead, search, dateFilter }) {
  const options = {
    page,
    pageSize,
    signal,
  };
  if (employeeId) options.employeeId = employeeId;
  if (selectedLead) {
    if (selectedLead.companyName && selectedLead.companyName !== "Client Enterprise") {
      options.companyName = selectedLead.companyName;
    }
    if (selectedLead.clientId) {
      options.clientId = selectedLead.clientId;
    }
    if (selectedLead.leadId) {
      options.leadId = selectedLead.leadId;
    }
  }
  if (search.trim()) options.search = search.trim();
  if (dateFilter) {
    options.dateFrom = dateFilter.format("YYYY-MM-DD");
    options.dateTo = dateFilter.format("YYYY-MM-DD");
  }
  return options;
}

function isLeadSelected(selectedLead, lead) {
  if (!selectedLead || !lead) return false;
  return Boolean(
    (selectedLead.key && lead.key && selectedLead.key === lead.key) ||
    (selectedLead.companyName && lead.companyName && selectedLead.companyName.toLowerCase() === lead.companyName.toLowerCase()) ||
    (selectedLead.clientId && lead.clientId && selectedLead.clientId === lead.clientId)
  );
}

function ProposalsView({ role: propRole }) {
  const { companyCode: effectiveCompanyCode, employeeId } = useAuthSession();

  const [proposals, setProposals] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [proposalCompanies, setProposalCompanies] = useState([]);
  const [railLoading, setRailLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAllProposalsCount, setTotalAllProposalsCount] = useState(0);

  const [selectedLead, setSelectedLead] = useState(null);
  const [propSearch, setPropSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFilter, setDateFilter] = useState(null);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [selectedProposalRecord, setSelectedProposalRecord] = useState(null);
  const [selectedProposalLead, setSelectedProposalLead] = useState(null);

  const activeAbortControllerRef = useRef(null);
  const activeRequestIdRef = useRef(0);
  const allClientsRef = useRef(allClients);

  useEffect(() => {
    allClientsRef.current = allClients;
  }, [allClients]);

  // Debounce search input by 250ms to prevent spamming queries
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(propSearch.trim());
      setCurrentPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [propSearch]);

  // Load clients metadata for enriching lead names and contacts
  const loadClientsList = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    try {
      const res = await getClients(effectiveCompanyCode);
      const list = res?.clients || res?.items || res?.data || (Array.isArray(res) ? res : []);
      setAllClients(list);
    } catch (err) {
      console.error("Failed to load clients for proposals rail:", err);
    }
  }, [effectiveCompanyCode]);

  useEffect(() => {
    loadClientsList();
  }, [loadClientsList]);

  // Load Live Proposals from Middleware with Race Condition Guard
  const loadProposals = useCallback(async () => {
    if (!effectiveCompanyCode) return;

    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    activeAbortControllerRef.current = controller;

    const currentRequestId = ++activeRequestIdRef.current;
    setLoading(true);

    try {
      const options = buildProposalQueryOptions({
        page: currentPage,
        pageSize,
        signal: controller.signal,
        employeeId,
        selectedLead,
        search: debouncedSearch,
        dateFilter,
      });

      const res = await getProposals(effectiveCompanyCode, options);

      if (currentRequestId !== activeRequestIdRef.current) {
        return;
      }

      if (res.success) {
        setProposals(res.proposals || []);
        setTotalCount(res.total || (res.proposals || []).length);
        if (!selectedLead && !debouncedSearch && !dateFilter) {
          setTotalAllProposalsCount(res.total || (res.proposals || []).length);
        }
      }
    } catch (err) {
      if (controller.signal.aborted || currentRequestId !== activeRequestIdRef.current) {
        return;
      }
      console.error("Failed to load proposals from middleware:", err);
      message.error("Failed to fetch live proposals.");
    } finally {
      if (currentRequestId === activeRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [effectiveCompanyCode, employeeId, selectedLead, debouncedSearch, dateFilter, currentPage, pageSize]);

  useEffect(() => {
    loadProposals();
  }, [loadProposals]);

  // Load fast proposal summary & accounts from high-performance MongoDB aggregation
  const loadProposalCompanies = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setRailLoading(true);
    try {
      const summaryRes = await getProposalSummary(effectiveCompanyCode, { employeeId });
      if (summaryRes?.success && Array.isArray(summaryRes.companies)) {
        setProposalCompanies(summaryRes.companies);
        setTotalAllProposalsCount(summaryRes.totalProposals || 0);
        return;
      }

      // Graceful fallback to proposal list
      const res = await getProposals(effectiveCompanyCode, { page: 1, pageSize: 100 });
      if (res.success) {
        const props = res.proposals || [];
        setTotalAllProposalsCount(res.total || props.length);
        const list = extractProposalCompanies(props, allClientsRef.current || []);
        setProposalCompanies(list);
      }
    } catch (err) {
      console.error("Failed to load proposal companies:", err);
    } finally {
      setRailLoading(false);
    }
  }, [effectiveCompanyCode, employeeId]);

  useEffect(() => {
    loadProposalCompanies();
  }, [loadProposalCompanies]);

  const filteredLeads = proposalCompanies;

  const handleSelectLead = (lead) => {
    setSelectedLead((prev) => {
      if (!lead) return null;
      if (isLeadSelected(prev, lead)) {
        return null;
      }
      return lead;
    });
    setCurrentPage(1);
  };

  const handlePropSearchChange = (val) => {
    setPropSearch(val);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (d) => {
    setDateFilter(d);
    setCurrentPage(1);
  };

  const handleOpenProposal = useCallback(
    (record) => {
      setSelectedProposalRecord(record);
      const matchedLead = filteredLeads.find(
        (l) => l.clientId === record.clientId || l.leadCompanyName === record.leadCompanyName
      );
      setSelectedProposalLead(
        matchedLead || {
          clientId: record.clientId,
          leadCompanyName: record.leadCompanyName,
          contactName: record.contactName,
          contactNumber: record.contactNumber,
          directorEmailAddress: record.directorEmailAddress,
        }
      );
      setGenerateModalOpen(true);
    },
    [filteredLeads]
  );

  const handleOpenMail = useCallback(
    (record) => {
      handleOpenProposal(record);
    },
    [handleOpenProposal]
  );

  const columns = [
    {
      title: "Proposal #",
      dataIndex: "proposalNumber",
      key: "proposalNumber",
      render: (val, r) => (
        <Flex vertical style={{ cursor: "pointer" }} onClick={() => handleOpenProposal(r)}>
          <Text strong style={{ color: "#2459d3", fontSize: "0.9rem" }}>
            {val || "PROP-NEW"}
          </Text>
          {r.templateName && (
            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
              Tpl: {r.templateName}
            </Text>
          )}
        </Flex>
      ),
    },
    {
      title: "Company / Lead",
      dataIndex: "leadCompanyName",
      key: "leadCompanyName",
      render: (val, r) => (
        <Flex vertical>
          <Text strong style={{ fontSize: "0.88rem", color: "#0f172a" }}>
            {val || r.companyName || "Client Enterprise"}
          </Text>
          <Text type="secondary" style={{ fontSize: "0.75rem" }}>
            {r.contactName || "—"} {r.contactNumber ? `(${r.contactNumber})` : ""}
          </Text>
        </Flex>
      ),
    },
    {
      title: "Date",
      dataIndex: "proposalDate",
      key: "proposalDate",
      render: (val, r) => {
        const d = val || r.createdAt;
        return (
          <Text style={{ fontSize: "0.82rem" }}>
            {d
              ? new Date(d).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </Text>
        );
      },
    },
    {
      title: "Title / Scope",
      dataIndex: "title",
      key: "title",
      render: (t, r) => <Text style={{ fontSize: "0.82rem", color: "#334155" }}>{t || r.templateName || "Enterprise Commercial Proposal"}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (st) => (
        <Tag color="blue" style={{ borderRadius: 10, fontWeight: 600 }}>
          {st || "Active"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, r) => (
        <Flex justify="end" gap={6}>
          {r.publicUrl && (
            <Tooltip title="View Online Proposal">
              <Button type="text" size="small" icon={<EyeOutlined style={{ color: "#2459d3" }} />} onClick={() => window.open(r.publicUrl, "_blank")} />
            </Tooltip>
          )}

          <Tooltip title="View & Download Proposal PDF">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined style={{ color: "#2459d3" }} />}
              onClick={() => handleOpenProposal(r)}
            />
          </Tooltip>

          <Tooltip title="Send to Client via Email">
            <Button
              type="text"
              size="small"
              icon={<MailOutlined style={{ color: "#2459d3" }} />}
              onClick={() => handleOpenMail(r)}
            />
          </Tooltip>
        </Flex>
      ),
    },
  ];

  return (
    <div className="proposals-workspace-container">
      {/* ── WORKSPACE SPLIT (LEFT RAIL + RIGHT TABLE) ── */}
      <div className="qa-workspace-frame">
        {/* ── LEFT RAIL: PROPOSAL ACCOUNTS ── */}
        <div className="qa-rail-pane">
          <Flex justify="space-between" align="center" className="qa-rail-header">
            <Flex align="center" gap={8}>
              <FormOutlined style={{ color: "#2459d3", fontSize: 16 }} />
              <Text strong style={{ fontSize: "0.95rem" }}>
                Proposals ({totalAllProposalsCount || totalCount})
              </Text>
            </Flex>
          </Flex>

          {/* Pinned All Proposals Option */}
          <div className="qa-rail-pinned">
            <button
              type="button"
              className={`qa-rail-item ${!selectedLead ? "active" : ""}`}
              onClick={() => handleSelectLead(null)}
              style={{ width: "100%", textAlign: "left", display: "block", fontFamily: "inherit" }}
            >
              <Text
                strong
                style={{
                  fontSize: "0.88rem",
                  color: !selectedLead ? "#2459d3" : "#0f172a",
                  display: "block",
                }}
              >
                All Proposals & Accounts
              </Text>
              <Flex justify="space-between" align="center" style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ display: "block", fontSize: "0.74rem" }}>
                  View full live proposal registry
                </Text>
                {(totalAllProposalsCount || totalCount) > 0 && (
                  <span className={`qa-count-badge ${!selectedLead ? "active" : ""}`}>
                    {totalAllProposalsCount || totalCount}
                  </span>
                )}
              </Flex>
            </button>
          </div>

          <div className="qa-rail-list">
            {railLoading && filteredLeads.length === 0 ? (
              <div style={{ padding: "40px 0", textAlign: "center" }}>
                <Spin size="small" tip="Loading proposals..." />
              </div>
            ) : filteredLeads.length === 0 ? (
              <Empty description="No accounts found" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: "20px 0" }} />
            ) : (
              filteredLeads.map((lead) => {
                const isSelected = isLeadSelected(selectedLead, lead);
                const displayName = lead.companyName || lead.primaryContact || lead.clientId || "Client Enterprise";
                const contactDetails = [lead.primaryContact, lead.phone].filter(Boolean).join(" • ");
                const count = lead.proposalCount || 0;

                return (
                  <button
                    type="button"
                    key={lead.key || lead.clientId}
                    className={`qa-rail-item ${isSelected ? "active" : ""}`}
                    onClick={() => handleSelectLead(lead)}
                    style={{ width: "100%", textAlign: "left", display: "block", fontFamily: "inherit" }}
                  >
                    <Flex vertical style={{ width: "100%" }}>
                      <Text
                        strong
                        style={{
                          fontSize: "0.88rem",
                          color: isSelected ? "#2459d3" : "#0f172a",
                          lineHeight: 1.35,
                          wordBreak: "break-word",
                        }}
                      >
                        {displayName}
                      </Text>
                      <Flex justify="space-between" align="center" style={{ width: "100%", marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: "0.74rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, paddingRight: 6 }}>
                          {contactDetails || "—"}
                        </Text>
                        {count > 0 && (
                          <span className={`qa-count-badge ${isSelected ? "active" : ""}`}>
                            {count}
                          </span>
                        )}
                      </Flex>
                    </Flex>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ── RIGHT CONTENT: LIVE PROPOSAL TABLE ── */}
        <div className="qa-content-pane">
          <Flex vertical gap={16}>
            {/* Header & Filter Row */}
            <Flex justify="space-between" align="center" wrap="wrap" gap={16} className="qa-content-header">
              <Flex vertical>
                <Title level={4} style={{ margin: 0 }}>
                  {selectedLead ? selectedLead.companyName || "Account Proposals" : "Live Proposal Documents"}
                </Title>
                <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                  {totalCount} proposals found
                </Text>
              </Flex>

              <Flex align="center" gap={12} wrap="wrap">
                <Input prefix={<SearchOutlined style={{ color: "#94a3b8" }} />} placeholder="Search proposals..." value={propSearch} onChange={(e) => handlePropSearchChange(e.target.value)} allowClear style={{ width: 180 }} />

                <DatePicker placeholder="Filter Date" value={dateFilter} onChange={(d) => handleDateFilterChange(d)} style={{ width: 140 }} />
              </Flex>
            </Flex>

            {/* Ant Design Proposals Table */}
            {loading && proposals.length === 0 ? (
              <div style={{ padding: "80px 0", textAlign: "center" }}>
                <Spin size="large" tip="Loading proposals..." />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={proposals}
                loading={{
                  spinning: loading,
                  tip: "Loading proposals...",
                  size: "large",
                }}
                rowKey="_id"
                locale={{
                  emptyText: <Empty description="No proposals found for current filters" style={{ padding: "4rem 0" }} />,
                }}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalCount,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} proposals`,
                  onChange: (page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                  },
                }}
                className="qa-antd-table"
                size="middle"
                scroll={{ x: 900 }}
              />
            )}
          </Flex>
        </div>
      </div>

      {/* Proposal Generator Modal with native design and Mail attachment support */}
      <ProposalGeneratorModal
        open={generateModalOpen}
        visible={generateModalOpen}
        onClose={() => {
          setGenerateModalOpen(false);
          setSelectedProposalRecord(null);
          setSelectedProposalLead(null);
        }}
        lead={selectedProposalLead || selectedLead}
        record={selectedProposalRecord}
        savedProposalRecord={selectedProposalRecord}
        companyCode={effectiveCompanyCode}
        onProposalSaved={() => {
          loadProposals();
          loadProposalCompanies();
        }}
        onProposalSent={() => {
          loadProposals();
          loadProposalCompanies();
        }}
      />
    </div>
  );
}

export default ProposalsView;
